import { useSyncExternalStore } from 'react'
import { getLocalStorage, readStorageJson, writeStorageJson } from '../../../../lib/local-storage'
import { parseHeldKiosks, type HeldKiosk } from './cart-control'

// Imports relatifs uniquement (pas de `~`) : testable en `node --test` (`held-kiosks.test.mjs`).

/**
 * Mémoire des caisses prises en main depuis ce poste (`localStorage`, partagé par ses onglets) : le
 * backend ne sait pas quel admin tient une caisse. Elle survit à un rechargement ou à un onglet
 * fermé, pour que la caisse reste rendable d'ici ; ailleurs, la caisse n'est qu'en lecture seule.
 *
 * Stockage indisponible ou plein (navigation privée, quota) : la mémoire vit dans la page, le temps
 * de la session. Le poste garde ses gestes sur la caisse ; `writeHeldKiosks` le signale.
 */
const HELD_KIOSKS_KEY = 'cameras:cart-control:held-kiosks'

const NO_HELD_KIOSKS: HeldKiosk[] = []
const listeners = new Set<() => void>()

// Instantané stable tant que la valeur stockée ne change pas : `useSyncExternalStore` compare par
// référence.
let snapshot: { raw: string | null; held: HeldKiosk[] } = { raw: null, held: NO_HELD_KIOSKS }
// Mémoire de la page quand le stockage refuse l'écriture ; `null` tant qu'il l'accepte.
let pageMemory: HeldKiosk[] | null = null

const readRaw = (): string | null => {
  try {
    return getLocalStorage()?.getItem(HELD_KIOSKS_KEY) ?? null
  } catch {
    return null
  }
}

export const readHeldKiosks = (): HeldKiosk[] => {
  if (pageMemory !== null) return pageMemory
  const raw = readRaw()
  if (raw !== snapshot.raw) {
    const held = parseHeldKiosks(readStorageJson(getLocalStorage(), HELD_KIOSKS_KEY))
    snapshot = { raw, held: held.length === 0 ? NO_HELD_KIOSKS : held }
  }
  return snapshot.held
}

/**
 * Remplace la mémoire du poste (`holdKiosk`, `releaseHeldKiosk`, `pruneHeldKiosks`). `false` : le
 * navigateur ne l'a pas gardée, elle ne vaut que pour cette page (à signaler à l'opérateur).
 */
export const writeHeldKiosks = (update: (held: readonly HeldKiosk[]) => HeldKiosk[]): boolean => {
  const next = update(readHeldKiosks())
  const persisted = writeStorageJson(getLocalStorage(), HELD_KIOSKS_KEY, next)
  pageMemory = persisted ? null : next
  for (const listener of listeners) listener()
  return persisted
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  // Un autre onglet du poste prend ou rend une caisse : la mémoire suit.
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === HELD_KIOSKS_KEY) listener()
  }
  globalThis.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    globalThis.removeEventListener('storage', onStorage)
  }
}

/** Caisses prises en main depuis ce poste, telles que mémorisées (cf. `activeHeldKiosks`). */
export const useHeldKiosks = (): HeldKiosk[] =>
  useSyncExternalStore(subscribe, readHeldKiosks, () => NO_HELD_KIOSKS)

/** Message de l'opérateur quand la mémoire du poste ne survit pas à la page. */
export const HELD_KIOSKS_NOT_PERSISTED =
  "Le navigateur n'a pas gardé la prise de main : ce poste tient la caisse jusqu'au rechargement de la page."
